import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ElectronAuthCallback - A simple page that displays after successful login
 * when the login was initiated from the CellCog Desktop Electron app.
 * 
 * The Electron app's BrowserWindow detects navigation to this URL
 * and extracts the token and email from the query parameters.
 * 
 * This page is never seen by regular web users.
 */
const ElectronAuthCallback = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const email = params.get('email') || '';
  const hasToken = !!params.get('token');

  useEffect(() => {
    // The Electron app should detect this URL and close the window.
    // If we're still here after 3 seconds, something went wrong.
    const timer = setTimeout(() => {
      // Fallback: try window.close() (works if opened by Electron)
      try {
        window.close();
      } catch {
        // Can't close - user is probably in a regular browser
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: '#fafafa',
      padding: '20px',
      textAlign: 'center',
    }}>
      {hasToken ? (
        <>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <h2 style={{ fontSize: '20px', marginBottom: '8px', color: '#333' }}>
            Signed in successfully!
          </h2>
          <p style={{ color: '#888', fontSize: '14px', marginBottom: '8px' }}>
            {email && `Welcome, ${email}`}
          </p>
          <p style={{ color: '#aaa', fontSize: '13px' }}>
            You can close this window and return to CellCog Desktop.
          </p>
          <p style={{ color: '#ddd', fontSize: '11px', marginTop: '16px' }}>
            This window will close automatically...
          </p>
        </>
      ) : (
        <>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ fontSize: '20px', marginBottom: '8px', color: '#333' }}>
            Authentication Error
          </h2>
          <p style={{ color: '#888', fontSize: '14px' }}>
            No authentication token received. Please try signing in again from CellCog Desktop.
          </p>
        </>
      )}
    </div>
  );
};

export default ElectronAuthCallback;
